import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Plus, Send, Clock, CheckCircle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

export default function BetaFeedbackModal({ isOpen, onClose }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // View states: 'list', 'create', 'view'
  const [viewState, setViewState] = useState('list');
  
  // New ticket state
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  
  // View ticket state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/tickets/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar tickets');
      const data = await res.json();
      setTickets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTickets();
      setViewState('list');
      setSelectedTicket(null);
    }
  }, [isOpen]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ subject, message })
      });

      if (!res.ok) throw new Error('Error al enviar el ticket');

      setSubject('');
      setMessage('');
      await fetchTickets();
      setViewState('list');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReplyTicket = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: replyMessage })
      });

      if (!res.ok) throw new Error('Error al responder');

      const updated = await res.json();
      setSelectedTicket(updated);
      setTickets(tickets.map(t => t.id === updated.id ? updated : t));
      setReplyMessage('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(5px)',
      zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: 'var(--bg-glass)', border: '1px solid var(--glass-border)',
        borderRadius: '16px', padding: '2rem', width: '90%', maxWidth: '600px',
        position: 'relative', maxHeight: '90vh', display: 'flex', flexDirection: 'column'
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: '1rem', right: '1rem',
          background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer'
        }}>
          <X size={24} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1.5rem' }}>
          <div style={{ background: 'rgba(139, 92, 246, 0.2)', padding: '0.5rem', borderRadius: '8px' }}>
            <MessageSquare size={24} color="#8b5cf6" />
          </div>
          <h2 style={{ margin: 0, color: 'white' }}>Centro de Soporte</h2>
        </div>

        {error && (
          <div style={{ padding: '0.8rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', borderRadius: '8px', color: '#ef4444', fontSize: '0.85rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {viewState === 'list' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem' }}>Mis Tickets</h3>
              <button 
                onClick={() => setViewState('create')}
                style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold' }}
              >
                <Plus size={16} /> Nuevo Ticket
              </button>
            </div>
            
            {loading && tickets.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>Cargando tickets...</p>
            ) : tickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>No tienes tickets de soporte activos.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {tickets.map(t => (
                  <div 
                    key={t.id} 
                    onClick={() => { setSelectedTicket(t); setViewState('view'); }}
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', padding: '1rem', cursor: 'pointer', transition: 'background 0.2s' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <h4 style={{ margin: 0, color: 'white' }}>{t.subject}</h4>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', padding: '0.2rem 0.6rem', borderRadius: '12px', background: t.status === 'resolved' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)', color: t.status === 'resolved' ? '#10b981' : '#f59e0b' }}>
                        {t.status === 'resolved' ? <CheckCircle size={12} /> : <Clock size={12} />}
                        {t.status === 'resolved' ? 'Resuelto' : 'Abierto'}
                      </span>
                    </div>
                    <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.message}</p>
                    <small style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '0.5rem' }}>{new Date(t.createdAt).toLocaleString()}</small>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {viewState === 'create' && (
          <form onSubmit={handleCreateTicket} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1 }}>
            <div>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 'bold' }}>Asunto</label>
              <input 
                type="text" 
                required 
                value={subject} 
                onChange={e => setSubject(e.target.value)}
                placeholder="Ej. Problema con la integración de Strava"
                style={{ width: '100%', boxSizing: 'border-box', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }}
              />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 'bold' }}>Mensaje</label>
              <textarea 
                required 
                value={message} 
                onChange={e => setMessage(e.target.value)}
                placeholder="Describe tu problema o sugerencia..."
                style={{ width: '100%', boxSizing: 'border-box', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px', flex: 1, minHeight: '150px', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: 'auto' }}>
              <button type="button" onClick={() => setViewState('list')} style={{ background: 'transparent', border: '1px solid var(--glass-border)', color: 'var(--text-muted)', padding: '0.8rem', borderRadius: '8px', cursor: 'pointer', flex: 1, fontWeight: 'bold' }}>Cancelar</button>
              <button type="submit" disabled={loading} style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '0.8rem', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', flex: 2, fontWeight: 'bold' }}>
                {loading ? 'Enviando...' : 'Enviar Ticket'}
              </button>
            </div>
          </form>
        )}

        {viewState === 'view' && selectedTicket && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid var(--glass-border)', paddingBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: 'white' }}>{selectedTicket.subject}</h3>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{new Date(selectedTicket.createdAt).toLocaleString()}</span>
              </div>
              <button onClick={() => setViewState('list')} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '0.4rem 0.8rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>Volver</button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem', paddingRight: '0.5rem' }}>
              {/* Mensaje original */}
              <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '8px', alignSelf: 'flex-start', maxWidth: '85%' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Tú</span>
                <p style={{ margin: 0, color: 'white', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{selectedTicket.message}</p>
              </div>

              {/* Respuestas */}
              {Array.isArray(selectedTicket.responses) ? selectedTicket.responses.map((r, i) => (
                <div key={i} style={{ 
                  background: r.isAdmin ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255,255,255,0.05)', 
                  border: r.isAdmin ? '1px solid rgba(139, 92, 246, 0.3)' : 'none',
                  padding: '1rem', borderRadius: '8px', 
                  alignSelf: r.isAdmin ? 'flex-end' : 'flex-start', maxWidth: '85%' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                    <span style={{ fontSize: '0.8rem', color: r.isAdmin ? '#c4b5fd' : 'var(--text-muted)', fontWeight: r.isAdmin ? 'bold' : 'normal' }}>
                      {r.isAdmin ? 'Soporte Técnico' : 'Tú'}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{new Date(r.createdAt).toLocaleString()}</span>
                  </div>
                  <p style={{ margin: 0, color: 'white', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{r.message}</p>
                </div>
              )) : (selectedTicket.responses ? JSON.parse(selectedTicket.responses).map((r, i) => (
                <div key={i} style={{ 
                  background: r.isAdmin ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255,255,255,0.05)', 
                  border: r.isAdmin ? '1px solid rgba(139, 92, 246, 0.3)' : 'none',
                  padding: '1rem', borderRadius: '8px', 
                  alignSelf: r.isAdmin ? 'flex-end' : 'flex-start', maxWidth: '85%' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                    <span style={{ fontSize: '0.8rem', color: r.isAdmin ? '#c4b5fd' : 'var(--text-muted)', fontWeight: r.isAdmin ? 'bold' : 'normal' }}>
                      {r.isAdmin ? 'Soporte Técnico' : 'Tú'}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{new Date(r.createdAt).toLocaleString()}</span>
                  </div>
                  <p style={{ margin: 0, color: 'white', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{r.message}</p>
                </div>
              )) : null)}
            </div>
            
            {selectedTicket.status !== 'resolved' && (
              <form onSubmit={handleReplyTicket} style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <input 
                  type="text" 
                  value={replyMessage}
                  onChange={e => setReplyMessage(e.target.value)}
                  placeholder="Escribe una respuesta..."
                  style={{ flex: 1, padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }}
                />
                <button type="submit" disabled={loading || !replyMessage.trim()} style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '0.8rem 1.2rem', borderRadius: '8px', cursor: (loading || !replyMessage.trim()) ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                  <Send size={18} />
                </button>
              </form>
            )}
            {selectedTicket.status === 'resolved' && (
              <div style={{ marginTop: '1rem', padding: '1rem', textAlign: 'center', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '8px' }}>
                Este ticket ha sido resuelto y cerrado.
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
